import config from "../config";
import {
  EXTENSION_OPENED_QUERY_VALUE,
  EXTENSION_OPENED_SESSION_STORAGE_KEY,
} from "../utils/extensionSession";
import { clearConnectorSnapshot } from "../utils/connectorSnapshotCache";
import { notifySessionCleared } from "./authLifecycle";
import { exchangeOAuthCodeOnce } from "./oauthExchangeDedup";
import { DESKTOP_STATE_SUFFIX } from "../utils/desktopCallback";

const clearStoredUserSession = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("user_info");
  localStorage.removeItem("selectedMode");
  // Cached connector rows are per-user; never leave them behind after sign-out/expiry.
  clearConnectorSnapshot();
  notifySessionCleared();
};

/**
 * True when the URL carries a Google login OAuth callback (not integration OAuth).
 *
 * @example
 * // Input: "?code=abc&state=login"
 * isLoginOAuthCallback("?code=abc&state=login");
 * // true
 *
 * @example
 * // Input: "?code=abc&state=business_gmail"
 * isLoginOAuthCallback("?code=abc&state=business_gmail");
 * // false
 */
export const isLoginOAuthCallback = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const code = params.get("code");
  const state = String(params.get("state") || "").trim().toLowerCase();
  return Boolean(code && (state === "login" || state === "login_ext"));
};

// The desktop app can't complete Google's sign-in inside its own embedded
// window (Google blocks embedded webviews), so it hands off to the system
// browser. Google redirects to the normal web origin (config.REDIRECT_URI,
// already an authorized redirect URI) with state=login_desktop2; the web app
// then moves the tab to /desktop-callback, which opens the app through the
// taskmantis:// link (see src/core/utils/desktopCallback.js). The main process
// in public/electron.js exchanges the code with this same redirect_uri.

// Deduplicate: OAuth codes are single-use; remounts otherwise return invalid_grant.
// redirectUri must match whatever redirect_uri was used to obtain this code
// from Google - the backend forwards it verbatim to Google's token endpoint,
// which requires an exact match.
const exchangeGoogleLoginCode = (code, redirectUri) =>
  exchangeOAuthCodeOnce("login", code, async (authCode) => {
    const response = await fetch(`${config.BACKEND_URL}/auth/google/callback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ code: authCode, redirect_uri: redirectUri }),
      credentials: "include",
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(await response.text());
    }

    return response.json();
  });

/**
 * Exchange a Google login OAuth code for a session.
 *
 * @example
 * // Input: window at "/?code=abc&state=login"
 * await exchangeLoginAuthCode();
 * // Output: { user_info: { email: "user@example.com", ... } }
 */
export const exchangeLoginAuthCode = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) {
    return null;
  }

  const state = String(params.get("state") || "").trim().toLowerCase();
  if (state === "login_ext") {
    window.sessionStorage.setItem(
      EXTENSION_OPENED_SESSION_STORAGE_KEY,
      EXTENSION_OPENED_QUERY_VALUE
    );
  }

  return exchangeGoogleLoginCode(code, config.REDIRECT_URI);
};

/**
 * Exchange a Google login OAuth code delivered via the desktop app's
 * taskmantis:// deep link (see public/electron.js) rather than a URL.
 *
 * @example
 * await exchangeDesktopLoginCode("abc123");
 * // Output: { user_info: { email: "user@example.com", ... } }
 */
export const exchangeDesktopLoginCode = (code) =>
  exchangeGoogleLoginCode(code, config.REDIRECT_URI);

/**
 * The ticket the Claude/MCP login page put on the onboarding link, if any.
 *
 * @example
 * // Input: "?return=http%3A%2F%2Fx&mcp_ticket=abc.def"
 * getMcpHandoffTicket("?return=http%3A%2F%2Fx&mcp_ticket=abc.def");
 * // "abc.def"
 */
export const getMcpHandoffTicket = (search = window.location.search) =>
  new URLSearchParams(search).get("mcp_ticket") || "";

/**
 * Drop the one-time ticket from the address bar, keeping every other param.
 *
 * @example
 * // Input: "?return=x&mcp_ticket=abc.def"
 * stripMcpHandoffTicket("?return=x&mcp_ticket=abc.def");
 * // "?return=x"
 */
export const stripMcpHandoffTicket = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  params.delete("mcp_ticket");
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

/**
 * Swap the MCP login ticket for a session, so a user already signed in with
 * Google on the Claude login page is not asked to sign in again.
 *
 * @example
 * // Input: window at "/onboarding?mcp_ticket=abc.def"
 * await exchangeMcpHandoff();
 * // Output: { user_info: { email: "user@example.com", ... } }, or null if the
 * // ticket is missing/expired (caller falls back to the normal sign-in)
 */
export const exchangeMcpHandoff = async () => {
  const ticket = getMcpHandoffTicket();
  if (!ticket) {
    return null;
  }
  return exchangeOAuthCodeOnce("mcp_handoff", ticket, async (value) => {
    const response = await fetch(`${config.BACKEND_URL}/auth/exchange-handoff`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ticket: value }),
      credentials: "include",
      mode: "cors",
    });
    if (!response.ok) {
      return null;
    }
    return response.json();
  });
};

// Function to sign in with Google
export const signInWithGoogle = () => {
  const isDesktop = Boolean(window.starmeshDesktop?.isElectron);
  const redirectUri = config.REDIRECT_URI;
  const state = isDesktop ? `login${DESKTOP_STATE_SUFFIX}` : "login"; // <<< identify the flow
  const googleAuthUrl =
    `https://accounts.google.com/o/oauth2/v2/auth` +
    `?client_id=${config.GOOGLE_CLIENT_ID}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_type=code` +
    `&scope=openid email profile` +
    `&state=${state}`;
  // Desktop: open Chrome via IPC so the SPA stays on app:// (window.location
  // would trip will-navigate and, with a bad app:// origin check, the OS).
  if (isDesktop && window.starmeshDesktop?.openExternal) {
    void window.starmeshDesktop.openExternal(googleAuthUrl);
    return;
  }
  window.location.href = googleAuthUrl;
};

// Function to handle the redirect after authentication
export const handleAuthRedirect = async () => {
  if (!isLoginOAuthCallback()) {
    return null;
  }
  return exchangeLoginAuthCode();
};

/**
 * Permanently delete the signed-in user's account.
 *
 * @example
 * // Input: authenticated session cookie
 * await deleteAccount();
 * // Output: redirects to "/" after server deletes account
 */
export const deleteAccount = async () => {
  const response = await fetch(`${config.BACKEND_URL}/account`, {
    method: "DELETE",
    credentials: "include",
    mode: "cors",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to delete account");
  }
  clearStoredUserSession();
  window.location.href = "/";
};

// Function to sign out the user
export const signOut = async () => {
  try {
    await fetch(`${config.BACKEND_URL}/logout`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
    });
  } catch (error) {
    // Best-effort server logout; still clear client state.
    console.error("Failed to call logout endpoint:", error);
  } finally {
    clearStoredUserSession();
    window.location.href = "/"; // Redirect to the sign-in page
  }
};
